import { ControlModeTemplate } from '@src/types/ControlModes';
import { ControlModes as StackControlModes } from '@src/types/Stacks';
import React, { useCallback } from 'react';
import {
  StackModalAction,
  StackModalActionType,
  StackModalState,
} from '../../types';

export const useControlModeActions = (
  state: StackModalState,
  dispatch: React.Dispatch<StackModalAction>,
  clearControlModeValidationError: (index: number, field?: string) => void,
) => {
  const setControlModes = useCallback(
    (controlModes: StackControlModes[]) => {
      dispatch({
        type: StackModalActionType.SET_CONTROL_MODES,
        payload: controlModes,
      });
    },
    [dispatch],
  );

  const handleAddControlMode = useCallback(() => {
    dispatch({ type: StackModalActionType.ADD_CONTROL_MODE });
  }, [dispatch]);

  const handleDuplicateControlMode = useCallback(
    (index: number) => {
      dispatch({
        type: StackModalActionType.DUPLICATE_CONTROL_MODE,
        payload: index,
      });
    },
    [dispatch],
  );

  const handleRemoveControlMode = useCallback(
    (index: number) => {
      dispatch({
        type: StackModalActionType.REMOVE_CONTROL_MODE,
        payload: index,
      });
    },
    [dispatch],
  );

  const handleControlModeNameChange = useCallback(
    (index: number, name: string) => {
      dispatch({
        type: StackModalActionType.UPDATE_CONTROL_MODE_NAME,
        payload: { index, name },
      });
    },
    [dispatch],
  );

  const handleControlModeAliasChange = useCallback(
    (index: number, name: string) => {
      dispatch({
        type: StackModalActionType.UPDATE_CONTROL_MODE_ALIAS,
        payload: { index, name },
      });
    },
    [dispatch],
  );

  const handleControlModeAliasRemoval = useCallback(
    (index: number) => {
      dispatch({
        type: StackModalActionType.REMOVE_CONTROL_MODE_ALIAS,
        payload: index,
      });
    },
    [dispatch],
  );

  const handleControlModeEnabledChange = useCallback(
    (index: number, enabled: boolean) => {
      dispatch({
        type: StackModalActionType.UPDATE_CONTROL_MODE_ENABLED,
        payload: { index, enabled },
      });
    },
    [dispatch],
  );

  const handleTemplatesSelected = useCallback(
    (
      templates: ControlModeTemplate[],
      controlModeIndex: number,
      templateIds?: string[],
    ) => {
      dispatch({
        type: StackModalActionType.TEMPLATES_SELECTED,
        payload: { templates, controlModeIndex, templateIds },
      });
    },
    [dispatch],
  );

  const handleControlModeNameChangeWithValidation = useCallback(
    (index: number, name: string) => {
      handleControlModeNameChange(index, name);
      if (name.trim() && state.validationErrors[`controlMode_${index}_name`]) {
        clearControlModeValidationError(index, 'name');
      }
    },
    [
      handleControlModeNameChange,
      state.validationErrors,
      clearControlModeValidationError,
    ],
  );

  return {
    setControlModes,
    handleAddControlMode,
    handleDuplicateControlMode,
    handleRemoveControlMode,
    handleControlModeNameChange,
    handleControlModeAliasChange,
    handleControlModeAliasRemoval,
    handleControlModeEnabledChange,
    handleTemplatesSelected,
    handleControlModeNameChangeWithValidation,
    handleDeleteControlMode: handleRemoveControlMode,
  };
};
